import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCatalogText } from '@/lib/localizeCatalog';
import Modal from '@/components/ui/Modal';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectCheckout, setCustomerNote, setCartOption, clearCartOptions } from './checkoutSlice';
import CartOptionsSection from './CartOptionsSection';
import { scrollToStep } from './CheckoutSteps';
import {
  cartOptionsTotalCents,
  groupsForAmount,
  groupsForMode,
  selectionsForGroups,
  unansweredRequiredGroups,
} from './cartOptions';
import { selectTableSession, selectIsBuffetDineIn } from '@/features/tableSession/tableSessionSlice';
import { useSharedTableCart } from '@/features/tableSession/useSharedTableCart';
import { resolveSharedCartLines, groupSharedCartLinesByGuest } from '@/features/tableSession/resolveSharedCart';
import { useSubmitSharedCartMutation } from '@/features/tableSession/sharedCartApi';
import { useGetMenuQuery } from '@/app/api/resourceApi';
import Price from '@/components/ui/Price';
import { showErrorToast } from '@/features/ui/uiSlice';
import TurnstileWidget, { type TurnstileWidgetHandle } from '@/features/user/AccountModal/TurnstileWidget';
import { persistOrderAccessToken, sentRoundsParam } from './ordersApi';
import ServiceChoiceButtons from './ServiceChoiceButtons';
import { useServiceChoiceQuestion } from './serviceChoice';
import type { ServiceChoice } from '@/types/order';
import { orderErrorMessage } from './orderErrors';

// Ancre de la carte des questions obligatoires : la barre d'envoi y renvoie
// quand c'est elle qui bloque (SC-760).
const SHARED_CART_OPTIONS_ID = 'shared-cart-options';

// SC-169: the shared-cart checkout. Dine-in already skips name/phone/payment, and
// the cart lives server-side — so this is a review-and-send screen: show the whole
// table's cart, then one button submits it as a single order. SC-427: in ordering
// mode it also shows per-line prices + a total before sending; buffet stays
// price-less (priceNote). The server total is authoritative on confirmation.
export default function SharedTableCheckout() {
  const { t } = useTranslation();
  const ct = useCatalogText();
  const { restaurantUuid = '' } = useParams<{ restaurantUuid: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const tableSession = useAppSelector(selectTableSession);
  const shared = useSharedTableCart();
  const tableToken = tableSession?.tableToken ?? '';

  const { data: menu } = useGetMenuQuery(
    restaurantUuid ?? '',
    { skip: !restaurantUuid },
  );
  const lines = resolveSharedCartLines(shared.lines, menu);
  const groups = groupSharedCartLinesByGuest(lines, shared.guestUuid);
  // SC-427: show per-line prices + a total before sending (ordering dine-in).
  // Buffet is à-volonté / price-less, so keep the priceNote there.
  const isBuffet = useAppSelector(selectIsBuffetDineIn);
  const showPrices = !isBuffet;
  const subtotal = lines.reduce((sum, l) => sum + l.unitPrice * l.qty, 0);
  const [submitSharedCart, { isLoading }] = useSubmitSharedCartMutation();
  // DF-0586 — when the table's cart spans several services, the confirm
  // sheet's send button becomes the two choices themselves.
  const serviceQuestion = useServiceChoiceQuestion(
    restaurantUuid,
    lines.map((line) => line.productUuid),
  );
  // SC-567: the shared cart skipped the whole CheckoutPage, so the guest had
  // nowhere to write a note. Reuse the checkout slice's customerNote (already
  // wiped when joining another table) and clear it after each send so round 2
  // does not silently resend round 1's note.
  const checkout = useAppSelector(selectCheckout);
  const customerNote = checkout.customerNote;
  // SC-753 — la table partagée est un mode de vente « Sur place » comme un autre :
  // les questions posées avant validation (couverts, sauce) doivent y être posées
  // aussi. Sans elles, une question obligatoire ciblant la salle faisait refuser
  // l'envoi par le devis, sans que personne n'ait pu y répondre.
  const cartOptionGroupsForMode = useMemo(
    () => groupsForMode(menu?.cartOptionGroups, 'dine_in'),
    [menu?.cartOptionGroups],
  );
  const cartOptionGroups = useMemo(
    () => groupsForAmount(cartOptionGroupsForMode, subtotal),
    [cartOptionGroupsForMode, subtotal],
  );
  const answeredCartOptions = useMemo(
    () => selectionsForGroups(cartOptionGroups, checkout.cartOptions),
    [cartOptionGroups, checkout.cartOptions],
  );
  const cartOptionsTotal = cartOptionsTotalCents(cartOptionGroups, checkout.cartOptions);
  const missingCartOptions = unansweredRequiredGroups(cartOptionGroups, checkout.cartOptions);
  // SC-188: any guest can send the whole table to the kitchen, so gate the
  // irreversible send behind an explicit confirmation dialog.
  const [confirmOpen, setConfirmOpen] = useState(false);

  // SC-337: Cloudflare Turnstile human-verification before sending the shared
  // cart (buffet / dine-in QR) to the kitchen — blocks bot spam. Managed widget
  // → invisible/auto for most guests; an interactive challenge only on high-risk
  // sessions. Gate is keyed on the site key being present (no key in dev/CI → no
  // widget, no gate; the backend verify also fails open there).
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? '';
  const needCaptcha = !!recaptchaSiteKey;
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  // Turnstile tokens are single-use: reset after a rejected/failed send so a
  // retry gets a fresh token.
  const resetCaptcha = () => { turnstileRef.current?.reset(); setRecaptchaToken(null); };

  const backToTable = `/${restaurantUuid}/table/${tableToken}`;

  // The widget sits just above the "Tout envoyer en cuisine" button; tapping the
  // button before the check completes is blocked with a clear message.
  const onSendClick = () => {
    if (lines.length === 0 || isLoading) return;
    if (needCaptcha && !recaptchaToken) {
      dispatch(showErrorToast(t('checkout.errors.recaptchaRequired')));
      return;
    }
    setConfirmOpen(true);
  };

  const onSubmit = async (serviceChoice?: ServiceChoice) => {
    if (lines.length === 0 || isLoading || missingCartOptions.length > 0) return;
    // The token can expire while the confirm dialog is open → block the send and
    // re-challenge so the guest can recover.
    if (needCaptcha && !recaptchaToken) {
      setConfirmOpen(false);
      resetCaptcha();
      dispatch(showErrorToast(t('checkout.errors.recaptchaRequired')));
      return;
    }
    try {
      const res = await submitSharedCart({
        guestToken: shared.guestToken,
        recaptchaToken,
        customerNote,
        cartOptions: answeredCartOptions,
        serviceChoice: serviceQuestion ? serviceChoice : undefined,
      }).unwrap();
      const orderId = res.order.uuid;
      persistOrderAccessToken(orderId, res.order_access_token);
      dispatch(setCustomerNote(''));
      // Comme la note : les réponses valent pour la tournée qu'on vient
      // d'envoyer, pas pour la suivante.
      dispatch(clearCartOptions());
      // SC-346: rounds merge into one anchor order, so pin the just-sent round in
      // the URL → the confirmation recap shows only these dishes, not earlier rounds.
      // DF-0586 — plus every service the table chose to hold back.
      const round = sentRoundsParam(
        (res.order.items ?? []).map((i) => i.round_number),
        res.order.held_rounds,
      );
      const params = new URLSearchParams();
      if (res.order_access_token) params.set('access_token', res.order_access_token);
      if (round != null) params.set('round', round);
      const qs = params.toString();
      navigate(`/${restaurantUuid}/table/${tableToken}/confirmation/${orderId}${qs ? `?${qs}` : ''}`);
    } catch (err) {
      setConfirmOpen(false);
      if (needCaptcha) resetCaptcha();
      // SC-574: the send drains into the very same `_handle_dine_in` as the
      // single-guest checkout, so it can come back with the whole dine-in reject
      // set (session expired, rounds exhausted, licence, buffet budget…). Mapping
      // only the captcha and the rate limit here meant every other reason reached
      // the table as "something went wrong" — share the checkout's mapping. No
      // cart lines are passed: the dine-in branch never names offending products
      // per uuid (only the online branch does), so there is nothing to resolve.
      dispatch(showErrorToast(orderErrorMessage(err, t)));
    }
  };

  const captchaEl = needCaptcha ? (
    <TurnstileWidget
      ref={turnstileRef}
      siteKey={recaptchaSiteKey}
      onSuccess={(token) => setRecaptchaToken(token)}
      onExpire={() => setRecaptchaToken(null)}
      onError={() => setRecaptchaToken(null)}
    />
  ) : null;

  return (
    <div className="max-w-[680px] mx-auto px-4 py-8 space-y-6">
      <h1 className="font-display text-3xl">{t('tableShared.cartTitle')}</h1>

      <div
        role="status"
        className="border-2 border-ink bg-cream p-3 font-mono text-sm shadow-stamp"
      >
        {t('tableShared.banner')}
      </div>

      {lines.length === 0 ? (
        <div className="border-2 border-ink bg-paper p-6 text-center font-mono text-sm text-ink/70 shadow-stamp">
          <p className="mb-4">{t('tableShared.empty')}</p>
          <Link
            to={backToTable}
            className="inline-block bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn px-4 py-2 font-display"
          >
            {t('cart.continueShopping')}
          </Link>
        </div>
      ) : (
        <section className="border-2 border-ink bg-paper p-5 shadow-stamp space-y-4">
          {groups.map((group) => (
            <div key={group.guestUuid || group.index}>
              <p className="font-display text-sm text-mint-deep m-0 mb-1">
                {group.isMine
                  ? t('tableShared.guestLabelYou', { n: group.index })
                  : t('tableShared.guestLabel', { n: group.index })}
              </p>
              <ul className="font-mono text-sm space-y-2 list-none p-0 m-0">
                {group.lines.map((line) => (
                  <li key={line.lineId} className="border-b-[1px] border-ink/20 pb-2 last:border-b-0">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-display">{line.qty}× {ct.text(line.name, menu?.items.find((i) => i.id === line.productUuid)?.translations)}</span>
                      {showPrices && (
                        <span className="font-display shrink-0">
                          <Price amountCents={line.unitPrice * line.qty} />
                        </span>
                      )}
                    </div>
                    {line.optionLabels.length > 0 && (
                      <ul className="list-none pl-4 m-0 text-xs text-ink/70 leading-tight">
                        {line.optionLabels.map((label, i) => (
                          <li key={i}>· {label}</li>
                        ))}
                      </ul>
                    )}
                    {line.comment && (
                      <p className="m-0 pl-4 text-xs italic text-ink/60">“{line.comment}”</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {showPrices ? (
            <div className="flex items-center justify-between border-t-2 border-ink pt-3 font-display text-lg">
              <span>{t('order.total')}</span>
              <Price amountCents={subtotal + cartOptionsTotal} />
            </div>
          ) : (
            <p className="font-mono text-xs text-ink/60">{t('tableShared.priceNote')}</p>
          )}
        </section>
      )}

      {/* SC-753 — questions posées sur la commande entière, avant la note :
          même composant et même place que sur la validation d'un seul convive. */}
      {lines.length > 0 && (
        <CartOptionsSection
          step={{
            id: SHARED_CART_OPTIONS_ID,
            label: t('checkout.cartOptions.title'),
            state: missingCartOptions.length > 0 ? 'blocked' : 'done',
          }}
          groups={cartOptionGroups}
          selections={checkout.cartOptions}
          onChange={(selection) => dispatch(setCartOption(selection))}
        />
      )}

      {/* SC-567: note for the kitchen — same field and wording as the
          single-guest dine-in checkout. Only useful once there is something to
          send, so it follows the cart section. */}
      {lines.length > 0 && (
        <section className="border-2 border-ink bg-paper p-5 shadow-stamp">
          <h2 className="font-display text-xl mb-3">{t('checkout.dineIn.noteTitle')}</h2>
          <textarea
            className="w-full border-2 border-ink p-2 font-mono text-sm bg-paper resize-none"
            rows={3}
            maxLength={280}
            data-testid="shared-customer-note"
            placeholder={t('cart.notePlaceholderTakeaway')}
            value={customerNote}
            onChange={(e) => dispatch(setCustomerNote(e.target.value.slice(0, 280)))}
          />
          <div className="mt-1 text-end font-mono text-xs text-ink/60">
            {customerNote.length} / 280
          </div>
        </section>
      )}

      {/* z-[410] keeps the bar (and the interactive Turnstile challenge) above
          the site-wide z-400 corner mascot on mobile — same fix as SC-315. */}
      <div className="sticky bottom-0 z-[410] bg-paper border-2 border-ink p-4 shadow-stamp space-y-2">
        <Link
          to={backToTable}
          className="font-mono text-xs underline text-ink/70"
          data-testid="shared-back-to-table"
        >
          {t('checkout.dineIn.retourTable')}
        </Link>
        {lines.length > 0 && (
          <>
            {/* SC-760 — même règle que la validation d'un seul convive : le
                bouton d'envoi ne reste jamais gris sans dire pourquoi. */}
            {missingCartOptions.length > 0 && (
              <div
                role="status"
                data-testid="shared-blocker"
                className="flex flex-wrap items-center justify-between gap-2 border-2 border-ink rounded-btn bg-ink px-3 py-2 font-display text-sm leading-snug text-paper"
              >
                <span>
                  <span aria-hidden="true">⚠</span>{' '}
                  {t('checkout.todo.banner', { label: t('checkout.cartOptions.title') })}
                </span>
                <button
                  type="button"
                  onClick={() => scrollToStep(SHARED_CART_OPTIONS_ID)}
                  className="flex-none rounded-btn-sm border-2 border-paper px-2.5 py-1 font-mono text-xs"
                >
                  {t('checkout.todo.cta')}
                </button>
              </div>
            )}
            <p className="font-mono text-sm text-ink/80">{t('tableShared.submitConfirm')}</p>
            {/* SC-337: human verification, just above the send button. */}
            {captchaEl && <div className="flex justify-center">{captchaEl}</div>}
            <button
              type="button"
              onClick={onSendClick}
              disabled={isLoading || lines.length === 0 || missingCartOptions.length > 0}
              className="w-full bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn py-3 font-display shadow-stamp-sm disabled:opacity-50"
            >
              {isLoading ? '…' : `${t('tableShared.submitBtn')} · ${shared.count}`}
            </button>
          </>
        )}
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => {
          if (!isLoading) setConfirmOpen(false);
        }}
        ariaLabel={t('tableShared.confirmTitle')}
      >
        <div className="p-6 space-y-4">
          <h2 className="font-display text-2xl m-0">{t('tableShared.confirmTitle')}</h2>
          <p className="font-mono text-sm text-ink/80 m-0">{t('tableShared.confirmBody')}</p>
          {serviceQuestion && (
            <ServiceChoiceButtons
              services={serviceQuestion}
              onChoose={(choice) => void onSubmit(choice)}
              disabled={isLoading}
            />
          )}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={isLoading}
              data-testid="shared-confirm-cancel"
              className="border-2 border-ink rounded-btn px-5 py-3 font-display bg-paper disabled:opacity-50"
            >
              {t('tableShared.confirmCancel')}
            </button>
            {!serviceQuestion && (
              <button
                type="button"
                onClick={() => void onSubmit()}
                disabled={isLoading}
                data-testid="shared-confirm-send"
                className="bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn px-5 py-3 font-display shadow-stamp-sm disabled:opacity-50"
              >
                {isLoading ? '…' : t('tableShared.confirmSend')}
              </button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
